import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
  StatementComposite,
  StatementCompositeTarget,
  StatementIssue,
} from '@repo/frontend-types';
import { asArray, capitaliseFirstLetter, entityIdentityFor } from '@repo/front-end-utils';

import { indexPlanByIdentity, LIVE_OBSERVER_MARKER } from './matchObserversToPlan';

/**
 * Put a running twin's complex measures against the entities on its plan.
 *
 * The composite half of `matchObserversToPlan`, and deliberately the same shape: a pure data
 * transformation, so the rule can be driven by a fixture taken off a real twin with no React and no
 * fetch.
 *
 * ## The server says what each input IS — this does not re-derive it
 *
 * A floor BID (`PUL-CON-Ground Floor`) and an entity BID are the same shape, so deciding which one
 * a string names is a rule, not a lookup. The fold has to apply that rule to resolve a member at
 * all, so it hands back the answer (`StatementCompositeTarget`) rather than a bare string. An
 * earlier version of this file re-derived it from the string, which put a second copy of the rule on
 * this side of the wire with nothing linking the two — and two copies that disagree is a measure
 * placed against a different thing from the observers it adds up (Rob, 2026-09-13).
 *
 * ## It shares the observers' index, and that is not an optimisation
 *
 * `indexPlanByIdentity` is imported rather than reimplemented. A composite says "area `A004` is the
 * sum of these entities", and the observers say "this entity is watched by that camera" — if the
 * two disagreed about which plan entity a BID names, a composite would end up adding up something
 * other than what its own observers measure, and nothing on screen would show it.
 *
 * ## A composite is placed whole or not at all — and "whole" includes what the SERVER dropped
 *
 * If the target or **any** input cannot be found on the plan, the composite is not placed and is
 * reported. Placing a half-mapped one would file a measure that adds up fewer things than it says.
 *
 * ⚠ **The plan is only half of that, and the other half nearly shipped.** By the time a composite
 * reaches here, any member the SERVER could not resolve has already been removed from `inputs` —
 * so the survivors all map, the whole-or-nothing rule passes, and the measure places cleanly while
 * being short a member. That is live production data: nine broken references across four gyms, so
 * opening Hull would have placed four measures each missing one and told the operator all of them
 * loaded. The statement's `issues` are therefore an INPUT to this decision, not just a report.
 *
 * ## Floors are matched by NAME, and that is a known weakness
 *
 * There is no floor identity on a plan yet (onboarding epic, `onboarding-floor-identity`), so a
 * floor-targeted composite is matched on the floor's name, trimmed and case-folded. A renamed floor
 * is therefore **reported as unplaced rather than guessed at**. Every one of the 17 floor composites
 * on production is named `{twinBid}-{floorName}`, which is where `floorName` comes from.
 */

/**
 * Marks a composite that came from the live configuration rather than being drawn in this session.
 *
 * Deliberately the same value as `LIVE_OBSERVER_MARKER`, and derived from it rather than retyped:
 * the two markers mean the same thing about two kinds of object, and a re-match tells loaded from
 * drawn by exactly the same test on both.
 */
export const LIVE_COMPOSITE_MARKER = LIVE_OBSERVER_MARKER;

/** `cmp:live-30` — the composite's database id, so the refid is stable across reloads. */
export const liveCompositeRefid = (id: number): string => `cmp${LIVE_COMPOSITE_MARKER}${id}`;

/**
 * Why a composite could not be placed (`onboarding-polish-observer-notices`, R4) — the facts, so a
 * surface that can name entities writes its own sentence with `unplacedCompositeSentence`.
 */
export type UnplacedCompositeKind =
  | 'server-problem'
  | 'unknown-target'
  | 'missing-target'
  | 'missing-member'
  | 'no-members';

/** What could not be found: an entity BID as stored, or a fragment already worded (a floor, a name). */
export type UnplacedMissing = { bid: string } | { phrase: string };

export interface UnplacedComposite {
  compositeId: number;
  name: string;
  kind: UnplacedCompositeKind;
  /** What the composite totals, as the statement classified it. */
  target: StatementCompositeTarget;
  /** For `missing-target` and `missing-member`. */
  missing?: UnplacedMissing;
  /** The server's own sentence, for `server-problem`. */
  detail?: string;
  /**
   * The sentence with every code as it is stored — what the Composite measures step shows, word
   * for word as before this type gained its facts (pinned by `matchCompositesToPlan.sentences.test.ts`).
   */
  reason: string;
}

/** A code to the text shown for it. */
type CodeNamer = (code: string) => string;

const asStored: CodeNamer = code => code;

/**
 * The sentence for a composite that could not be placed.
 *
 * With no namer it is the sentence the wizard has always written, every code as stored, and
 * `reason` is exactly that. With one (the Observers notices, `onboarding-polish-observer-notices`
 * R4) every entity code is named *Name (CODE)*, and a total whose stored name is only its target's
 * code (every composite on staging Finsbury Park, measured 2026-10-06) is called *the total for
 * {its target}* instead. The server's own sentence (`server-problem`) is carried through untouched.
 */
export const unplacedCompositeSentence = (
  u: Omit<UnplacedComposite, 'reason'>,
  nameFor?: CodeNamer,
): string => {
  const label = nameFor ?? asStored;
  const missing =
    u.missing === undefined ? '' : 'bid' in u.missing ? label(u.missing.bid) : u.missing.phrase;

  /*
   * The subject: as stored, `“{name}”`, unless a namer is given AND the stored name says nothing
   * the target's code does not. Then the target is named instead.
   */
  const targetCode = u.target.kind === 'entity' || u.target.kind === 'floor' ? u.target.bid : null;
  const stored = u.name.trim();
  const nameIsCode =
    !stored || (targetCode !== null && stored.toUpperCase() === targetCode.trim().toUpperCase());
  let derived: string | null = null;
  if (nameFor && nameIsCode) {
    if (u.target.kind === 'entity') derived = `the total for ${label(u.target.bid)}`;
    else if (u.target.kind === 'floor') derived = `the total for the floor “${u.target.floorName}”`;
    else if (u.target.kind === 'twin') derived = 'the whole-twin total';
  }
  const subject = derived ? capitaliseFirstLetter(derived) : `“${u.name}”`;

  switch (u.kind) {
    case 'server-problem':
      return u.detail ?? '';
    case 'unknown-target':
      return `${subject} is not named for this twin, one of its entities, or one of its floors, so there is nothing on the plan to attach it to.`;
    case 'missing-target':
      // Named after its own missing target, the usual sentence would say the same thing twice.
      return derived && (u.target.kind === 'entity' || u.target.kind === 'floor')
        ? `${subject} is a total for something this plan does not have.`
        : `${subject} is a total for ${missing}, which this plan does not have.`;
    case 'missing-member':
      return `${subject} adds up ${missing}, which this plan does not have, so it cannot be placed without changing what it measures.`;
    case 'no-members':
      return `${subject} has no members this plan can resolve, so it would add up nothing.`;
    default: {
      const exhaustive: never = u.kind;
      return exhaustive;
    }
  }
};

/** An unplaced composite, its `reason` written with every code as stored. */
const unplacedOf = (u: Omit<UnplacedComposite, 'reason'>): UnplacedComposite => ({
  ...u,
  reason: unplacedCompositeSentence(u),
});

export interface MatchCompositesResult {
  /** Ready for `setComposites`. */
  composites: FloorPlannerComposite[];
  /** Those that could not be put on the plan. Stated to the operator, logged in full. */
  unplaced: UnplacedComposite[];
  /** Placed successfully — the number the stage announces. */
  placed: number;
}

/**
 * Whether the server said this total also counts its target's own cameras.
 *
 * ⚠ **Read through a local widening rather than by adding the field to `StatementComposite`.**
 * `statement-file` owns that type change (its R1a) and will make it on both halves at once — the
 * shared type, the middleware's response class, and the `Record<keyof StatementComposite, true>`
 * guard `statement-read` put in `twin-sense-statement.controller.spec.ts`. Adding it here to the
 * shared type alone breaks that guard at COMPILE time and nothing reports it: `backend-tests` takes
 * its computed skip for a diff that touches no backend path, and `nest build` excludes spec files.
 * The runtime half of the same guard then stays green while the type and the published Swagger
 * contract have actually diverged — the check written to catch exactly this reporting success.
 *
 * So this feature reads the field and declares nothing. Absent is `false`, which is what a
 * statement produced before `statement-file` lands actually is.
 */
const readIncludesOwnObservers = (composite: StatementComposite): boolean =>
  (composite as StatementComposite & { includesOwnObservers?: boolean }).includesOwnObservers ===
  true;

export const matchCompositesToPlan = (
  layout: FloorPlannerLayout | null,
  twinBid: string | undefined,
  composites: StatementComposite[],
  /**
   * The statement's issues. `unresolvable-member`, `duplicate-member` and `ambiguous-member` are
   * read: each means the server already removed something from `inputs`, so the composite in front
   * of us is not the measure the running gym has.
   */
  issues: StatementIssue[],
): MatchCompositesResult => {
  const placedComposites: FloorPlannerComposite[] = [];
  const unplaced: UnplacedComposite[] = [];

  const index = indexPlanByIdentity(layout, twinBid);

  /*
   * Composite ids the server reported a member problem against, and what it said.
   *
   * ⚠ **All three member kinds block, and `ambiguous-member` is the one that is easy to forget** —
   * it was added to the statement after this filter was first written, and was missing from it.
   * The fold does not pick a winner when a member name matches several observers watching different
   * entities: it drops the member and reports it. So an ambiguous measure's surviving inputs can all
   * map cleanly, and without this the wizard would place and count a measure that is adding up less
   * than the gym is. That is the same silent shortfall `unresolvable-member` causes, arrived at by a
   * different route.
   */
  const BLOCKING_MEMBER_ISSUES: ReadonlySet<StatementIssue['kind']> = new Set<
    StatementIssue['kind']
  >(['unresolvable-member', 'duplicate-member', 'ambiguous-member']);
  const serverProblemByCompositeId = new Map<number, string>();
  for (const issue of issues) {
    if (
      BLOCKING_MEMBER_ISSUES.has(issue.kind) &&
      issue.compositeId != null &&
      !serverProblemByCompositeId.has(issue.compositeId)
    ) {
      serverProblemByCompositeId.set(issue.compositeId, issue.detail);
    }
  }

  /*
   * Floor name -> floor id.
   *
   * ⚠ **A name two floors share is AMBIGUOUS, not first-writer-wins.** The entity index can take the
   * first writer because an entity BID is unique by definition; a floor name is not unique by
   * anything, and a plan is free to carry "Mezzanine" twice. Taking the first would attach a floor
   * total to whichever storey happened to be earlier in the layout, silently, and this file's own
   * rule is that a floor it cannot resolve is reported rather than guessed at — the same rule that
   * refuses a renamed one. Tracked separately from "no such floor" and from "has the floor but no
   * identity", because all three send a reader somewhere different.
   *
   * ⚠ **A floor with no `id` is tracked separately rather than folded into "not found".** Every
   * plan on staging carries one (`twin_plan` for twin 522 holds `{id: 1, name: "Ground Floor"}`),
   * but `id` is not something this code writes, and "the plan has no such floor" and "the plan has
   * that floor but it has no identity" are different problems with different fixes. Reporting the
   * second as the first sends whoever reads it looking for a floor that is on the screen in front
   * of them.
   */
  const floorIdByName = new Map<string, number>();
  const floorNamesWithNoId = new Set<string>();
  const ambiguousFloorNames = new Set<string>();
  for (const floor of asArray(layout?.floors)) {
    const name = (floor?.name ?? '').trim().toUpperCase();
    if (!name) continue;
    if (typeof floor.id !== 'number') {
      floorNamesWithNoId.add(name);
      continue;
    }
    const seen = floorIdByName.get(name);
    if (seen === undefined) floorIdByName.set(name, floor.id);
    else if (seen !== floor.id) ambiguousFloorNames.add(name);
  }

  /** Resolve one classified reference to a plan reference, or say why it could not be. */
  const resolve = (
    ref: StatementCompositeTarget,
  ): { ok: true; value: FloorPlannerCompositeRef } | { ok: false; missing: UnplacedMissing } => {
    if (ref.kind === 'twin') return { ok: true, value: { kind: 'twin' } };
    if (ref.kind === 'unknown') return { ok: false, missing: { phrase: `“${ref.name}”` } };

    if (ref.kind === 'floor') {
      const name = ref.floorName.trim().toUpperCase();
      if (ambiguousFloorNames.has(name)) {
        return {
          ok: false,
          missing: {
            phrase: `the floor “${ref.floorName}”, which this plan has more than one of — so which storey the total belongs to cannot be determined`,
          },
        };
      }
      const floorId = floorIdByName.get(name);
      if (floorId != null) return { ok: true, value: { kind: 'floor', floorId } };
      return {
        ok: false,
        missing: {
          phrase: floorNamesWithNoId.has(name)
            ? `the floor “${ref.floorName}”, which this plan has but carries no identity for`
            : `the floor “${ref.floorName}”`,
        },
      };
    }

    const target = index.get(entityIdentityFor(twinBid, ref.bid));
    return target
      ? { ok: true, value: { kind: 'entity', refid: target.refid, entityKind: target.kind } }
      : { ok: false, missing: { bid: ref.bid } };
  };

  for (const composite of composites) {
    const { target } = composite;

    const serverProblem = serverProblemByCompositeId.get(composite.id);
    if (serverProblem !== undefined) {
      // Not placed, for exactly the reason a half-mapped one is not: it would file a number that is
      // not what the gym measures. The server's own sentence is carried through, because it names
      // the member.
      unplaced.push(
        unplacedOf({
          compositeId: composite.id,
          name: composite.name,
          kind: 'server-problem',
          target,
          detail: serverProblem,
        }),
      );
      continue;
    }

    if (target.kind === 'unknown') {
      unplaced.push(
        unplacedOf({
          compositeId: composite.id,
          name: composite.name,
          kind: 'unknown-target',
          target,
        }),
      );
      continue;
    }

    const resolvedTarget = resolve(target);
    if (!resolvedTarget.ok) {
      unplaced.push(
        unplacedOf({
          compositeId: composite.id,
          name: composite.name,
          kind: 'missing-target',
          target,
          missing: resolvedTarget.missing,
        }),
      );
      continue;
    }

    // Whole or not at all: the first input that cannot be found stops the composite being placed.
    const inputs: FloorPlannerCompositeRef[] = [];
    let missingInput: UnplacedMissing | null = null;
    for (const input of composite.inputs) {
      const resolved = resolve(input);
      if (!resolved.ok) {
        missingInput = resolved.missing;
        break;
      }
      inputs.push(resolved.value);
    }

    if (missingInput !== null) {
      unplaced.push(
        unplacedOf({
          compositeId: composite.id,
          name: composite.name,
          kind: 'missing-member',
          target,
          missing: missingInput,
        }),
      );
      continue;
    }

    if (inputs.length === 0 && !readIncludesOwnObservers(composite)) {
      /*
       * A measure that adds up nothing. Reached when the stored member list is empty or unreadable —
       * the fold emits the composite with `inputs: []` by design. Counting it as "loaded" would
       * put a number on screen for a measure with no members, and `go-live-statement` would file it.
       *
       * ⚠ **Unless it counts its target's own cameras, which IS a measure with no members.**
       * The wizard's editor says so in as many words — with the own-cameras tick on, a total over
       * its target's cameras alone is complete, and `validateCompositeForm` accepts it. Without this
       * exemption the two halves of one feature contradict each other: an operator creates such a
       * measure, it saves, filing writes it, and the next read of the configuration reports it
       * "has no members this plan can resolve" and drops it off the plan. Found by Copilot's review
       * of this branch, and it is the shape the fold produces for a partial group.
       *
       * (The epic's `completeness-and-coverage` introduces an empty composite as a valid DRAFT. That
       * is a thing a person creates in the wizard, not a thing a read of a running configuration can
       * produce, so it does not change this.)
       */
      unplaced.push(
        unplacedOf({
          compositeId: composite.id,
          name: composite.name,
          kind: 'no-members',
          target,
        }),
      );
      continue;
    }

    placedComposites.push({
      refid: liveCompositeRefid(composite.id),
      target: resolvedTarget.value,
      inputs,
      /*
       * ⚠ Carried, not derived — and losing it is not cosmetic.
       *
       * The fold strips a target out of its own inputs and raises `self-input`, so a measure that
       * counts its own entity's cameras alongside other entities arrives here looking exactly like
       * one that does not. Nine active composites on five production gyms are that shape. Drop this
       * line and `go-live-statement` files them without their own cameras — a number that silently
       * goes down, on a gym that thinks nothing changed.
       */
      ...(readIncludesOwnObservers(composite) ? { includesOwnObservers: true } : {}),
      ...(composite.excluded ? { excluded: true } : {}),
    });
  }

  return { composites: placedComposites, unplaced, placed: placedComposites.length };
};
